import { useCallback, useEffect, useMemo, useState } from 'react';
import { describeError, getVaptAiUsage, type VaptAiUsageResponse } from '@/lib/api';
import { CardTable, THead, TBody, Tr, Th, Td } from '@/components/CardTable';

/**
 * Consumo do Vapt IA no mês (change vapt-ai-agent, §5.4).
 *
 * A pergunta que a tela responde é sempre a mesma — "dá para deixar ligado?" —
 * e ela tem duas metades: quanto a plataforma já gastou contra o teto, e se
 * existe um tenant fora da curva puxando o número sozinho. Por isso o medidor
 * vem antes da tabela, e a tabela vem ordenada por consumo, não por nome.
 *
 * A resposta traz **só contagens**. Não há texto de conversa, argumento de
 * ferramenta nem nome de item — um vazamento aqui seria conteúdo comercial de
 * um lojista aparecendo em outro contexto. Ver `CONTRIBUTING.md`, tabela de
 * rotas cross-tenant.
 */

const ALERT_PERCENT = 80;

const intFmt = new Intl.NumberFormat('pt-BR');
const usdFmt = new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'USD' });
const pctFmt = new Intl.NumberFormat('pt-BR', { minimumFractionDigits: 1, maximumFractionDigits: 1 });
const monthFmt = new Intl.DateTimeFormat('pt-BR', { month: 'long', year: 'numeric' });

function currentMonth(): string {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
}

/** Últimos 12 meses, do mais recente para o mais antigo. */
function monthOptions(): Array<{ value: string; label: string }> {
  const out: Array<{ value: string; label: string }> = [];
  const now = new Date();
  for (let i = 0; i < 12; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const value = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    out.push({ value, label: monthFmt.format(d) });
  }
  return out;
}

export function VaptAiUsagePage() {
  const [month, setMonth] = useState(currentMonth);
  const [data, setData] = useState<VaptAiUsageResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const months = useMemo(() => monthOptions(), []);

  useEffect(() => {
    let cancelled = false;
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setLoading(true);
    setError(null);
    getVaptAiUsage(month)
      .then((res) => !cancelled && setData(res))
      .catch((err) => !cancelled && setError(describeError(err)))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [month]);

  const tenants = useMemo(
    () => [...(data?.tenants ?? [])].sort((a, b) => b.estimatedCostUsd - a.estimatedCostUsd),
    [data],
  );

  const onMonthChange = useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => setMonth(e.target.value),
    [],
  );

  return (
    <div className="page">
      <h1 style={{ marginTop: 0 }}>Uso do Vapt IA</h1>

      <div className="filter-inline">
        <label className="field">
          <span>Mês</span>
          <select value={month} onChange={onMonthChange} aria-label="Mês de referência">
            {months.map((m) => (
              <option key={m.value} value={m.value}>
                {m.label}
              </option>
            ))}
          </select>
        </label>
      </div>

      {error && (
        <div className="alert alert-error" role="alert">
          {error}
        </div>
      )}
      {loading && !data && <p className="muted">Carregando…</p>}

      {data && (
        <>
          <BudgetCard platform={data.platform} />
          <TenantsCard tenants={tenants} />
          <KillSwitchNote />
        </>
      )}
    </div>
  );
}

function BudgetCard({ platform }: { platform: VaptAiUsageResponse['platform'] }) {
  const percent = platform.usedPercent;
  const over = percent >= 100;
  const alerting = percent >= ALERT_PERCENT;
  const tone = over ? 'error' : alerting ? 'warning' : 'success';

  return (
    <section className="card">
      <div className="card-title">Plataforma no mês</div>

      <div
        className="stat-grid"
        style={{ marginBottom: 'var(--space-4, 16px)' }}
      >
        <Stat label="Tokens de entrada" value={intFmt.format(platform.inputTokens)} />
        <Stat label="Tokens de saída" value={intFmt.format(platform.outputTokens)} />
        <Stat label="Custo estimado" value={usdFmt.format(platform.estimatedCostUsd)} />
      </div>

      <div
        role="meter"
        aria-valuenow={Math.round(percent)}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-label="Consumo do teto mensal de tokens"
        style={{
          height: 10,
          borderRadius: 999,
          background: 'var(--color-border)',
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            width: `${Math.min(100, Math.max(0, percent))}%`,
            height: '100%',
            background: over
              ? 'var(--color-error)'
              : alerting
                ? 'var(--color-warning)'
                : 'var(--color-success)',
          }}
        />
      </div>

      <p className="muted" style={{ marginTop: 8 }}>
        {intFmt.format(platform.inputTokens + platform.outputTokens)} de{' '}
        {intFmt.format(platform.budgetTokens)} tokens — {pctFmt.format(percent)}% do teto
      </p>

      {alerting && (
        <div className={`alert alert-${tone === 'error' ? 'error' : 'warning'}`} role="status">
          {over ? (
            <>
              <strong>Teto estourado.</strong> Mensagens novas estão sendo recusadas com{' '}
              <code>vapt_ai_budget_exhausted</code>. Decidir aprovação pendente e abrir conversa
              nova continuam funcionando — de propósito, para ninguém ficar com uma proposta presa.
              Ver runbook, seção 10.5.
            </>
          ) : (
            <>
              <strong>Passou de {ALERT_PERCENT}% do teto.</strong> Ainda funciona, mas vale decidir
              agora entre aumentar o teto e conter quem está fora da curva.
            </>
          )}
        </div>
      )}
    </section>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="muted">{label}</div>
      <div style={{ fontSize: '1.25rem', fontWeight: 600 }}>{value}</div>
    </div>
  );
}

function TenantsCard({ tenants }: { tenants: VaptAiUsageResponse['tenants'] }) {
  if (tenants.length === 0) {
    return (
      <section className="card">
        <div className="card-title">Por loja</div>
        <p className="muted">Nenhum consumo registrado neste mês.</p>
      </section>
    );
  }

  return (
    <section className="card card-flush">
      <div className="card-title" style={{ padding: '16px 16px 0' }}>
        Por loja
      </div>
      <CardTable>
        <THead>
          <Tr>
            <Th>Loja</Th>
            <Th>Turnos</Th>
            <Th>Entrada</Th>
            <Th>Saída</Th>
            <Th>Custo estimado</Th>
          </Tr>
        </THead>
        <TBody>
          {tenants.map((t) => (
            <Tr key={t.tenantId}>
              <Td heading>{t.tenantName}</Td>
              <Td label="Turnos">{intFmt.format(t.turns)}</Td>
              <Td label="Entrada">{intFmt.format(t.inputTokens)}</Td>
              <Td label="Saída">{intFmt.format(t.outputTokens)}</Td>
              <Td label="Custo estimado">{usdFmt.format(t.estimatedCostUsd)}</Td>
            </Tr>
          ))}
        </TBody>
      </CardTable>
    </section>
  );
}

function KillSwitchNote() {
  return (
    <section className="card">
      <div className="card-title">Como desligar</div>
      <p>
        Para derrubar o Vapt IA em toda a plataforma, desligue o padrão da flag global{' '}
        <strong>vapt-ai-enabled</strong> em <em>Feature flags</em>.
      </p>
      <p className="muted">
        Desligar <strong>panel.vapt-ai</strong> não basta: por ser uma flag por loja, o estado da
        loja substitui o padrão, então desligar o padrão não alcança justamente as lojas do
        piloto, que têm exceção ligada. Para tirar uma loja específica, remova a exceção dela.
      </p>
    </section>
  );
}

export default VaptAiUsagePage;
